import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { User } from '../types';
import { canSeeSitemap } from '../utils/roleUtils';

interface Organization {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  administrators: string[];
  administratorNames: string[];
  createdBy: string;
  createdByEmail: string;
  createdAt: any;
}

interface Program {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  managers: string[];
  managerNames: string[];
  organizationId?: string;
  organizationName?: string;
  createdBy: string;
  createdByEmail: string;
  createdAt: any;
}

interface Group {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  managers: string[];
  managerNames: string[];
  organizationId?: string;
  organizationName?: string;
  programId?: string;
  programName?: string;
  maxMembers?: number;
  category: string;
  status: 'active' | 'inactive' | 'full' | 'completed';
  meetingSchedule?: string;
  location?: string;
  createdBy: string;
  createdByEmail: string;
  createdAt: any;
}

interface SitemapProps {
  user: User;
}

export default function Sitemap({ user }: SitemapProps) {
  const { t, i18n } = useTranslation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set());

  // Load organizations
  useEffect(() => {
    const q = query(collection(db, 'organizations'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const orgsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Organization[];
      setOrganizations(orgsData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load programs
  useEffect(() => {
    const q = query(collection(db, 'programs'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const programsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Program[];
      setPrograms(programsData);
    });

    return () => unsubscribe();
  }, []);

  // Load groups
  useEffect(() => {
    const q = query(collection(db, 'groups'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const groupsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Group[];
      setGroups(groupsData);
    });

    return () => unsubscribe();
  }, []);

  const toggleOrganization = (orgId: string) => {
    const newExpanded = new Set(expandedOrgs);
    if (newExpanded.has(orgId)) {
      newExpanded.delete(orgId);
    } else {
      newExpanded.add(orgId);
    }
    setExpandedOrgs(newExpanded);
  };

  const toggleProgram = (programId: string) => {
    const newExpanded = new Set(expandedPrograms);
    if (newExpanded.has(programId)) {
      newExpanded.delete(programId);
    } else {
      newExpanded.add(programId);
    }
    setExpandedPrograms(newExpanded);
  };

  const getProgramsForOrg = (orgId: string) => {
    return programs.filter(p => p.organizationId === orgId);
  };

  const getGroupsForProgram = (programId: string) => {
    return groups.filter(g => g.programId === programId);
  };

  const getOrphanedPrograms = () => {
    return programs.filter(p => !p.organizationId);
  };

  const getOrphanedGroups = () => {
    return groups.filter(g => !g.programId);
  };

  // Check if user has permission to view sitemap
  if (!canSeeSitemap(user)) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#d32f2f', marginBottom: '10px' }}>
          ⚠️ {t('sitemap.noPermission') || 'You do not have permission to view this page'}
        </div>
        <p style={{ color: '#666' }}>
          {t('sitemap.noPermissionDesc') || 'Only Program Managers and Department Managers can access the Geographic Distribution page.'}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>
          {t('common.loading')}...
        </div>
      </div>
    );
  }

  const isRTL = i18n.language === 'he';

  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: '1400px', 
      margin: '0 auto',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: 600, color: '#333' }}>
          {t('sitemap.title')}
        </h2>
        <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
          {t('sitemap.subtitle')}
        </p>
      </div>

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '30px'
      }}>
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>
            {organizations.length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {t('sitemap.organizations')}
          </div>
        </div>
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>
            {programs.length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {t('sitemap.programs')}
          </div>
        </div>
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          border: '2px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px', color: '#333' }}>
            {groups.length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {t('sitemap.groups')}
          </div>
        </div>
      </div>

      {/* Hierarchy Tree */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#333' }}>
          {t('sitemap.hierarchy')}
        </h3>

        {organizations.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#999'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏢</div>
            <p>{t('sitemap.noData')}</p>
          </div>
        ) : (
          <div>
            {organizations.map((org) => {
              const orgPrograms = getProgramsForOrg(org.id);
              const isOrgExpanded = expandedOrgs.has(org.id);

              return (
                <div key={org.id} style={{ marginBottom: '16px' }}>
                  {/* Organization */}
                  <div
                    onClick={() => toggleOrganization(org.id)}
                    style={{
                      padding: '16px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      border: '2px solid #e0e7ff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f0f4ff';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8f9fa';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>
                      {isOrgExpanded ? '📂' : '📁'}
                    </span>
                    {org.logoUrl && (
                      <img
                        src={org.logoUrl}
                        alt={org.name}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', fontSize: '16px', color: '#1e293b' }}>
                        🏢 {org.name}
                      </div>
                      {org.description && (
                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                          {org.description}
                        </div>
                      )}
                    </div>
                    <div style={{
                      background: '#e0e7ff',
                      color: '#4338ca',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      {orgPrograms.length} {t('sitemap.programs')}
                    </div>
                    <span style={{ 
                      fontSize: '20px', 
                      transform: isOrgExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      display: 'inline-block'
                    }}>
                      {isRTL ? '◀' : '▶'}
                    </span>
                  </div>

                  {/* Programs under this Organization */}
                  {isOrgExpanded && (
                    <div style={{ 
                      [isRTL ? 'marginRight' : 'marginLeft']: '40px',
                      marginTop: '12px',
                      borderLeft: isRTL ? 'none' : '2px solid #e0e7ff',
                      borderRight: isRTL ? '2px solid #e0e7ff' : 'none',
                      [isRTL ? 'paddingRight' : 'paddingLeft']: '20px'
                    }}>
                      {orgPrograms.length === 0 ? (
                        <div style={{
                          padding: '16px',
                          background: '#fef3c7',
                          borderRadius: '8px',
                          color: '#92400e',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}>
                          {t('sitemap.noPrograms')}
                        </div>
                      ) : (
                        orgPrograms.map((program) => {
                          const programGroups = getGroupsForProgram(program.id);
                          const isProgramExpanded = expandedPrograms.has(program.id);

                          return (
                            <div key={program.id} style={{ marginBottom: '12px' }}>
                              {/* Program */}
                              <div
                                onClick={() => toggleProgram(program.id)}
                                style={{
                                  padding: '14px',
                                  background: 'white',
                                  borderRadius: '8px',
                                  border: '2px solid #fee2e2',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#fef2f2';
                                  e.currentTarget.style.transform = 'translateX(4px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'white';
                                  e.currentTarget.style.transform = 'translateX(0)';
                                }}
                              >
                                <span style={{ fontSize: '18px' }}>
                                  {isProgramExpanded ? '📂' : '📁'}
                                </span>
                                {program.logoUrl && (
                                  <img
                                    src={program.logoUrl}
                                    alt={program.name}
                                    style={{
                                      width: '28px',
                                      height: '28px',
                                      borderRadius: '6px',
                                      objectFit: 'cover'
                                    }}
                                  />
                                )}
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '600', fontSize: '15px', color: '#1e293b' }}>
                                    📋 {program.name}
                                  </div>
                                  {program.description && (
                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                      {program.description}
                                    </div>
                                  )}
                                </div>
                                <div style={{
                                  background: '#fee2e2',
                                  color: '#991b1b',
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}>
                                  {programGroups.length} {t('sitemap.groups')}
                                </div>
                                <span style={{ 
                                  fontSize: '16px',
                                  transform: isProgramExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease',
                                  display: 'inline-block'
                                }}>
                                  {isRTL ? '◀' : '▶'}
                                </span>
                              </div>

                              {/* Groups under this Program */}
                              {isProgramExpanded && (
                                <div style={{ 
                                  [isRTL ? 'marginRight' : 'marginLeft']: '40px',
                                  marginTop: '12px',
                                  borderLeft: isRTL ? 'none' : '2px solid #fee2e2',
                                  borderRight: isRTL ? '2px solid #fee2e2' : 'none',
                                  [isRTL ? 'paddingRight' : 'paddingLeft']: '20px'
                                }}>
                                  {programGroups.length === 0 ? (
                                    <div style={{
                                      padding: '12px',
                                      background: '#fef3c7',
                                      borderRadius: '8px',
                                      color: '#92400e',
                                      fontSize: '13px',
                                      textAlign: 'center'
                                    }}>
                                      {t('sitemap.noGroups')}
                                    </div>
                                  ) : (
                                    programGroups.map((group) => (
                                      <div
                                        key={group.id}
                                        style={{
                                          padding: '12px',
                                          background: 'white',
                                          borderRadius: '8px',
                                          border: '2px solid #e0f2fe',
                                          marginBottom: '8px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '12px'
                                        }}
                                      >
                                        <span style={{ fontSize: '16px' }}>👥</span>
                                        {group.logoUrl && (
                                          <img
                                            src={group.logoUrl}
                                            alt={group.name}
                                            style={{
                                              width: '24px',
                                              height: '24px',
                                              borderRadius: '6px',
                                              objectFit: 'cover'
                                            }}
                                          />
                                        )}
                                        <div style={{ flex: 1 }}>
                                          <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>
                                            {group.name}
                                          </div>
                                          {group.description && (
                                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                              {group.description}
                                            </div>
                                          )}
                                        </div>
                                        <div style={{
                                          background: '#e0f2fe',
                                          color: '#075985',
                                          padding: '4px 10px',
                                          borderRadius: '12px',
                                          fontSize: '11px',
                                          fontWeight: '500'
                                        }}>
                                          {group.status}
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Orphaned Programs (not assigned to any organization) */}
        {getOrphanedPrograms().length > 0 && (
          <div style={{ marginTop: '32px' }}>
            <div style={{
              background: '#fef3c7',
              padding: '16px',
              borderRadius: '8px',
              border: '2px solid #fbbf24',
              marginBottom: '12px'
            }}>
              <div style={{ fontWeight: '600', fontSize: '16px', color: '#92400e', marginBottom: '8px' }}>
                ⚠️ {t('sitemap.orphanedPrograms')}
              </div>
              <div style={{ fontSize: '13px', color: '#92400e' }}>
                {t('sitemap.orphanedProgramsDesc')}
              </div>
            </div>
            {getOrphanedPrograms().map((program) => (
              <div
                key={program.id}
                style={{
                  padding: '12px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '2px solid #fbbf24',
                  marginBottom: '8px',
                  [isRTL ? 'marginRight' : 'marginLeft']: '20px'
                }}
              >
                📋 {program.name}
              </div>
            ))}
          </div>
        )}

        {/* Orphaned Groups (not assigned to any program) */}
        {getOrphanedGroups().length > 0 && (
          <div style={{ marginTop: '32px' }}>
            <div style={{
              background: '#fef3c7',
              padding: '16px',
              borderRadius: '8px',
              border: '2px solid #fbbf24',
              marginBottom: '12px'
            }}>
              <div style={{ fontWeight: '600', fontSize: '16px', color: '#92400e', marginBottom: '8px' }}>
                ⚠️ {t('sitemap.orphanedGroups')}
              </div>
              <div style={{ fontSize: '13px', color: '#92400e' }}>
                {t('sitemap.orphanedGroupsDesc')}
              </div>
            </div>
            {getOrphanedGroups().map((group) => (
              <div
                key={group.id}
                style={{
                  padding: '12px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '2px solid #fbbf24',
                  marginBottom: '8px',
                  [isRTL ? 'marginRight' : 'marginLeft']: '20px'
                }}
              >
                👥 {group.name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

